import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import OtpCodeInput from "./OtpCodeInput";
import { requestActionOtp, verifyActionOtp, otpError } from "../../services/otpApi";
import "./Otp.css";

// =====================================================
// OTP CONFIRMATION DIALOG   (Parts 9 and 10)
//
// The confirmation step in front of every money operation:
//     Add Bank Details  → Email OTP → Verify → Save
//     Edit Bank Details → Email OTP → Verify → Allow edit → Save
//     Withdraw reward   → Email OTP → Verify → Request created
//
// It is ONE component used by the Refer & Earn wallet and by the Pro Bank
// screen, for the same reason the backend has one OTP service: three copies of
// "ask for a code, then call the thing" is three places for the resend
// cooldown or the error handling to drift.
//
// ── HOW IT IS USED ──
//   <OtpVerifyModal
//      open={showOtp}
//      purpose={OTP_PURPOSE.BANK_ADD}
//      title="Confirm your bank details"
//      onCancel={() => setShowOtp(false)}
//      onVerified={(ticket) => save(ticket)}   // ← the guarded action
//   />
//
// The parent does NOT perform its action until onVerified fires, and then it
// passes the ticket along as `otpTicket`. The ticket is the whole point: the
// server spends it before it writes anything, so a client that skipped this
// dialog has nothing to present and saves nothing.
//
// ── WHY THE EMAIL IS NEVER A PROP ──
// The code goes to the signed-in account's own address, read from the database
// by the server. If this component could name the recipient, "confirm by email"
// would prove nothing at all. It only DISPLAYS the masked address the server
// reports back.
// =====================================================

function OtpVerifyModal({
  open,
  purpose,
  title = "Confirm it's really you",
  lead = "",
  confirmLabel = "Verify & continue",
  onVerified,
  onCancel,
}) {
  const [code, setCode] = useState("");
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [invalid, setInvalid] = useState(false);
  const [maskedEmail, setMaskedEmail] = useState("");
  const [cooldown, setCooldown] = useState(0);
  const [devCode, setDevCode] = useState("");

  // The dialog sends a code the moment it opens. This guards React 18's double
  // effect invocation in StrictMode, which would otherwise burn two of the
  // user's five hourly sends before they had typed anything.
  const requestedFor = useRef(null);
  const submittingRef = useRef(false);

  const sendCode = useCallback(
    async ({ silent } = {}) => {
      setSending(true);
      if (!silent) {
        setError("");
        setNotice("");
      }

      try {
        const res = await requestActionOtp(purpose);
        setMaskedEmail(res.emailMasked || "");
        setNotice(res.message || "Code sent ✅");
        setCooldown(res.cooldownSeconds || 60);
        setDevCode(res.devCode || "");
        setCode("");
      } catch (err) {
        const e = otpError(err, "Could not send the code.");
        setError(e.message);
        if (e.retryAfterSeconds) setCooldown(Math.min(e.retryAfterSeconds, 300));
      } finally {
        setSending(false);
      }
    },
    [purpose]
  );

  // Open → send. Close → forget everything, so re-opening never shows a stale
  // code, a stale error or a countdown from the previous attempt.
  useEffect(() => {
    if (!open) {
      requestedFor.current = null;
      setCode("");
      setError("");
      setNotice("");
      setDevCode("");
      setMaskedEmail("");
      setCooldown(0);
      return;
    }

    if (requestedFor.current === purpose) return;
    requestedFor.current = purpose;
    sendCode();
  }, [open, purpose, sendCode]);

  useEffect(() => {
    if (!open || cooldown <= 0) return undefined;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [open, cooldown]);

  // Escape closes, like every other dialog in the app.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape" && !verifying) onCancel();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, verifying, onCancel]);

  const verify = async (value) => {
    const entered = String(value || code).replace(/\D/g, "");
    if (entered.length < 4) {
      setError("Enter the code we emailed you.");
      return;
    }
    if (submittingRef.current) return;

    submittingRef.current = true;
    setVerifying(true);
    setError("");
    setInvalid(false);

    try {
      const res = await verifyActionOtp(purpose, entered);
      // The parent performs the guarded action with this ticket. It is single
      // use and short-lived, so it is handed straight over rather than stored.
      onVerified(res.ticket);
    } catch (err) {
      const e = otpError(err, "That code could not be verified.");
      setError(e.message);
      setInvalid(true);
      setCode("");
      setTimeout(() => setInvalid(false), 400);
    } finally {
      submittingRef.current = false;
      setVerifying(false);
    }
  };

  if (!open) return null;

  const busy = sending || verifying;

  // ── WHY A PORTAL ──
  // One of the callers renders this dialog from INSIDE the bank-details <form>
  // (that is where the confirmation belongs in the code), and a <form> nested
  // inside another <form> is invalid HTML — the browser silently drops the
  // inner one, which would break the code entry. A portal puts this markup at
  // document.body, so the DOM never nests.
  //
  // React events still propagate along the REACT tree, not the DOM tree, so the
  // submit below is stopped explicitly — otherwise it would bubble into the
  // parent form's onSubmit and re-open this very dialog.
  return createPortal(
    <div
      className="otp-modal-overlay"
      onClick={() => {
        if (!verifying) onCancel();
      }}
      role="presentation"
    >
      <div
        className="otp-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <div className="otp-modal-head">
          <h3 className="otp-modal-title">{title}</h3>
          <button
            type="button"
            className="otp-modal-close"
            onClick={onCancel}
            disabled={verifying}
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        {lead && <p className="otp-modal-lead">{lead}</p>}

        <p className="otp-sent-to">
          {maskedEmail ? (
            <>We emailed a 6-digit code to <strong>{maskedEmail}</strong></>
          ) : sending ? (
            "Sending your code…"
          ) : (
            "Enter the code we emailed you."
          )}
        </p>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            // See the portal note above: without this the event reaches the
            // caller's own form handler through the React tree.
            e.stopPropagation();
            verify();
          }}
        >
          <OtpCodeInput
            value={code}
            onChange={(v) => {
              setCode(v);
              if (error) setError("");
            }}
            disabled={busy}
            invalid={invalid}
            onComplete={(v) => verify(v)}
            label="Confirmation code"
          />

          {error && <p className="otp-error">{error}</p>}
          {!error && notice && <p className="otp-ok">{notice}</p>}

          <div className="otp-modal-actions">
            <button
              type="button"
              className="otp-btn otp-btn-ghost"
              onClick={onCancel}
              disabled={verifying}
            >
              Cancel
            </button>
            <button type="submit" className="otp-btn otp-btn-primary" disabled={busy}>
              {verifying ? "Verifying…" : confirmLabel}
            </button>
          </div>
        </form>

        <div className="otp-resend-row">
          <button
            type="button"
            className="otp-resend-btn"
            onClick={() => sendCode()}
            disabled={cooldown > 0 || busy}
          >
            {cooldown > 0 ? `Resend code in ${cooldown}s` : "Resend code"}
          </button>
          <span className="otp-hint">The code expires in 10 minutes.</span>
        </div>

        {devCode && (
          <div className="otp-dev-banner">
            DEV MODE — no mail provider configured.<br />
            Your code: <strong>{devCode}</strong>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}

export default OtpVerifyModal;
